import { Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { IaChamadoHttpService } from 'src/app/services/ia-chamado-http.service';

interface IWindow extends Window {
  SpeechRecognition?: new () => any;
  webkitSpeechRecognition?: new () => any;
}

export interface MensagemChat {
  id: string;
  tipo: 'usuario' | 'assistente';
  conteudo: string;
  logId?: string;
  avaliacao?: 'like' | 'dislike';
  carregando?: boolean;
  cancelada?: boolean;
}

@Component({
  selector: 'app-modal-ai-milvus',
  templateUrl: './modal-ai-milvus.component.html',
  styleUrls: ['./modal-ai-milvus.component.scss']
})
export class ModalAiMilvusComponent implements OnDestroy {
  @ViewChild('scrollArea') scrollArea: ElementRef<HTMLElement>;
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;

  mensagens: MensagemChat[] = [];
  mensagemAtual = '';
  carregando = false;
  gravando = false;
  sessaoId: string | null = null;
  private newSubs: Subscription;
  private recognizer: any = null;
  private textoAntesGravacao = '';
  private timeoutSilencio: any = null;

  get temSpeechRecognition(): boolean {
    const w = window as IWindow;
    return typeof window !== 'undefined' &&
      (typeof w.SpeechRecognition !== 'undefined' || typeof w.webkitSpeechRecognition !== 'undefined');
  }

  frasesSugeridas = [
    'FRASE_SUGERIDA_TICKETS_7_DIAS',
    'FRASE_SUGERIDA_TICKETS_SLA_ESTOURADO'
  ];

  constructor(
    private activeModal: NgbActiveModal,
    private translate: TranslateService,
    private iaChamadoService: IaChamadoHttpService,
    private toastr: ToastrService,
    private ngZone: NgZone
  ) { }

  get imagemIconeAi(): string {
    return 'assets/img/icons/common/milvus_openai_chat.png';
  }


  ngOnDestroy(): void {
    this.newSubs?.unsubscribe();
    this.pararGravacao();
  }

  iniciarGravacao(): void {
    if (!this.temSpeechRecognition || this.gravando || this.carregando) return;
    this.textoAntesGravacao = this.mensagemAtual;
    const w = window as IWindow;
    const SpeechRecognitionClass = w.SpeechRecognition || w.webkitSpeechRecognition;
    this.recognizer = new SpeechRecognitionClass();
    this.recognizer.continuous = true;
    this.recognizer.interimResults = true;
    const lang = this.translate.currentLang === 'en' ? 'en-US' : this.translate.currentLang === 'es' ? 'es-ES' : 'pt-BR';
    this.recognizer.lang = lang;
    this.recognizer.onresult = (event: any): void => {
      this.ngZone.run(() => {
        this.resetarTimeoutSilencio();
        let textoReconhecido = '';
        for (let i = 0; i < event.results.length; i++) {
          textoReconhecido += event.results[i][0]?.transcript || '';
        }
        this.mensagemAtual = (this.textoAntesGravacao + ' ' + textoReconhecido).trim();
      });
    };
    this.recognizer.onerror = (event: any): void => {
      this.ngZone.run(() => {
        if (event.error !== 'aborted') {
          this.toastr.show(this.translate.instant('ERRO_RECONHECIMENTO_VOZ'), '', environment.toastrConfig('danger'));
        }
        this.gravando = false;
      });
    };
    this.recognizer.onend = (): void => {
      this.ngZone.run(() => {
        this.gravando = false;
      });
    };
    try {
      this.recognizer.start();
      this.gravando = true;
      this.resetarTimeoutSilencio();
    } catch {
      this.toastr.show(this.translate.instant('ERRO_RECONHECIMENTO_VOZ'), '', environment.toastrConfig('danger'));
    }
  }

  private resetarTimeoutSilencio(): void {
    if (this.timeoutSilencio) {
      clearTimeout(this.timeoutSilencio);
    }
    this.timeoutSilencio = setTimeout(() => {
      this.ngZone.run(() => {
        if (this.gravando) {
          this.pararGravacao();
        }
      });
    }, 2000);
  }

  toggleGravacao(): void {
    if (this.gravando) {
      this.pararGravacao();
    } else {
      this.iniciarGravacao();
    }
  }

  pararGravacao(): void {
    if (this.timeoutSilencio) {
      clearTimeout(this.timeoutSilencio);
      this.timeoutSilencio = null;
    }
    if (this.recognizer && this.gravando) {
      try {
        this.recognizer.stop();
      } catch { }
      this.recognizer = null;
      this.gravando = false;
    }
  }

  enviarMensagem(mensagem?: string): void {
    if (this.gravando) {
      this.pararGravacao();
    }
    if (this.carregando) {
      this.pausarConsulta();
      return;
    }

    const texto = (mensagem || this.mensagemAtual || '').trim();
    if (!texto) return;

    this.mensagemAtual = '';
    const msgUsuario: MensagemChat = {
      id: 'u-' + Date.now(),
      tipo: 'usuario',
      conteudo: texto
    };
    this.mensagens.push(msgUsuario);
    this.scrollToBottom();

    const msgAssistente: MensagemChat = {
      id: 'a-' + Date.now(),
      tipo: 'assistente',
      conteudo: '',
      carregando: true
    };
    this.mensagens.push(msgAssistente);
    this.carregando = true;

    this.newSubs = this.iaChamadoService.consultar(texto, this.sessaoId || undefined).subscribe({
      next: (res) => {
        if (res.sessao_id) {
          this.sessaoId = res.sessao_id;
        }
        const idx = this.mensagens.findIndex(m => m.id === msgAssistente.id);
        if (idx >= 0) {
          this.mensagens[idx] = {
            ...msgAssistente,
            conteudo: res.resposta || '',
            logId: res.log_id,
            carregando: false
          };
        }
        this.carregando = false;
        this.scrollToBottom();
      },
      error: (err) => {
        const msgErro = err?.mensagem
          ? err.mensagem
          : this.translate.instant('ERRO_CONSULTA_IA');
        const idx = this.mensagens.findIndex(m => m.id === msgAssistente.id);
        if (idx >= 0) {
          this.mensagens[idx] = {
            ...msgAssistente,
            conteudo: msgErro,
            carregando: false
          };
        }
        this.carregando = false;
        this.toastr.show(msgErro, '', environment.toastrConfig('danger'));
      }
    });
  }

  usarFraseSugerida(frase: string): void {
    this.enviarMensagem(this.translate.instant(frase));
  }

  copiarResposta(msg: MensagemChat): void {
    if (!msg.conteudo) return;
    navigator.clipboard.writeText(msg.conteudo).then(() => {
      this.toastr.show(this.translate.instant('COPIADO_SUCESSO'), '', environment.toastrConfig('success'));
    });
  }

  avaliarResposta(msg: MensagemChat, avaliacao: 'like' | 'dislike'): void {
    if (!msg.logId) return;
    msg.avaliacao = avaliacao;
    this.newSubs = this.iaChamadoService.avaliar(msg.logId, avaliacao).subscribe({
      next: () => { },
      error: () => {
        msg.avaliacao = undefined;
      }
    });
  }

  pausarConsulta(): void {
    this.newSubs?.unsubscribe();
    this.newSubs = null as any;
    this.carregando = false;
    const idx = this.mensagens.findIndex(m => m.carregando);
    if (idx >= 0) {
      this.mensagens[idx] = {
        ...this.mensagens[idx],
        conteudo: this.translate.instant('CONSULTA_CANCELADA'),
        carregando: false,
        cancelada: true
      };
    }
  }

  close(): void {
    this.activeModal.close();
  }

  scrollToBottom(): void {
    const scroll = (): void => {
      const area = this.scrollArea?.nativeElement;
      const container = this.scrollContainer?.nativeElement;
      if (area) area.scrollTop = area.scrollHeight;
      if (container) container.scrollTop = container.scrollHeight;
    };
    setTimeout(scroll, 0);
    setTimeout(scroll, 100);
    setTimeout(scroll, 250);
  }
}
